import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import { quizService } from '../services/quizService';
import { DbQuiz, DbQuestion, supabase, initializeStorage, HotspotData } from '../lib/supabase';
import { generateAudioServerSide } from '../services/audioJobService';
import { logger } from '../utils/logger';

export type { HotspotData };
export type Hotspot = HotspotData;

export type QuestionType = 'multiple-choice' | 'two_truths_and_a_lie' | 'truefalse' | 'video' | 'information' | 'policy' | 'click-to-reveal' | 'click-to-reveal-images' | 'drag-and-drop' | 'hover-reveal' | 'click-for-answer';

/**
 * Types that can actually be marked. A type only belongs here if its question
 * component reports the user's answer back via onAnswerSelect — otherwise the
 * answer never reaches the scorer and the question is unwinnable.
 *
 * click-to-reveal, click-to-reveal-images, hover-reveal and drag-and-drop are
 * interactive content: they hold their state locally and never call
 * onAnswerSelect, so they are presented but not marked. Counting them towards a
 * score put weight in the denominator that nobody could ever earn, which capped
 * affected modules below their pass mark.
 */
export const MARKABLE_QUESTION_TYPES: QuestionType[] = [
  'multiple-choice',
  'truefalse',
  'video',
  'two_truths_and_a_lie',
];

export const isMarkableQuestionType = (type: QuestionType) => MARKABLE_QUESTION_TYPES.includes(type);

/** A questions row (from the table or learner_questions()) as the app's Question. */
export const mapDbQuestion = (q: DbQuestion): Question => ({
  id: String(q.id),
  type: q.type,
  text: q.text,
  thumbnailUrl: q.thumbnail_url || '',
  options: q.options || [],
  correctOptions: (q.correct_options || []).map(String),
  correctCount: q.correct_count ?? (q.correct_options || []).length,
  explanation: q.explanation || '',
  numberOfGuesses: q.number_of_guesses,
  hotspots: q.hotspots || [],
  videoUrl: q.video_url || '',
  videoType: q.video_type as 'file' | 'url' | undefined,
  videoStartTime: q.video_start_time || 0,
  videoEndTime: q.video_end_time || 0,
  order: q.order,
  minimumReadTime: q.type === 'information' ? (q.minimum_read_time ?? 15) : q.minimum_read_time,
  imageOrientation: q.image_orientation as 'landscape' | 'portrait' | undefined,
  policyUrl: q.policy_url || '',
  // Audio support fields
  questionAudioUrl: q.question_audio_url || '',
  explanationAudioUrl: q.explanation_audio_url || '',
  questionAudioDuration: q.question_audio_duration,
  explanationAudioDuration: q.explanation_audio_duration,
  useAudio: q.use_audio || false,
  // Video question specific audio fields
  videoIntroAudioUrl: q.video_intro_audio_url || '',
  videoIntroAudioDuration: q.video_intro_audio_duration,
  videoQuestionAudioUrl: q.video_question_audio_url || '',
  videoQuestionAudioDuration: q.video_question_audio_duration,
  keyTakeawaysHeading: q.key_takeaways_heading || 'Key Takeaways',
  answerQuestion: q.answer_question || '',
  answerText: q.answer_text || '',
  audioTextHash: q.audio_text_hash || undefined,
  weight: q.weight || 0,
});

export interface Question {
  id: string;
  type: QuestionType;
  thumbnailUrl: string;
  text: string;
  options?: string[];
  correctOptions?: string[];
  // How many options are correct. Learners get marked questions without
  // correctOptions (check_answer reveals them on Submit), but the player
  // still needs this to offer "pick one" or "pick all that apply".
  correctCount?: number;
  explanation?: string;
  numberOfGuesses?: number;
  hotspots?: Hotspot[];
  videoUrl?: string;
  videoType?: 'file' | 'url';
  videoStartTime?: number;
  videoEndTime?: number;
  order?: number;
  minimumReadTime?: number; // Minimum time in seconds that users must spend on informational content
  weight?: number; // Weight/percentage of this question in overall quiz score (0-100)
  imageOrientation?: 'landscape' | 'portrait'; // Orientation of the image for information content
  policyUrl?: string; // URL to the full policy document
  // Audio support fields
  questionAudioUrl?: string; // URL to pre-recorded MP3 file for question narration
  explanationAudioUrl?: string; // URL to pre-recorded MP3 file for explanation narration
  questionAudioDuration?: number; // Duration of question audio in seconds (for timing sync)
  explanationAudioDuration?: number; // Duration of explanation audio in seconds (for timing sync)
  useAudio?: boolean; // Whether to use pre-recorded audio instead of TTS for this question
  // Video question specific audio fields
  videoIntroAudioUrl?: string; // URL to pre-recorded MP3 for video intro (e.g., "Please watch the video")
  videoIntroAudioDuration?: number; // Duration of video intro audio in seconds
  videoQuestionAudioUrl?: string; // URL to pre-recorded MP3 for question after video ends
  videoQuestionAudioDuration?: number; // Duration of video question audio in seconds
  keyTakeawaysHeading?: string; // Custom heading for the key takeaways section
  // Click for Answer fields
  answerQuestion?: string; // The question for click-for-answer type
  answerText?: string; // The answer for click-for-answer type
  audioTextHash?: string; // SHA-256 hash of text used to generate audio (for stale detection)
}

export interface Quiz {
  id: string;
  title: string;
  description: string;
  passingScore: number;
  mediaUrl?: string;
  questions: Question[];
  isEnabled: boolean;
  isHidden: boolean;
  order: number;
  hasIntroduction?: boolean;
  introductionTitle?: string;
  introductionContent?: string;
  introductionMediaUrl?: string;
  /** Seconds a learner must spend on the introduction. 0 / undefined = none. */
  introductionMinReadTime?: number;
  /** Minutes allowed from Start Module to finish. null / undefined = no limit. */
  timeLimitMinutes?: number | null;
  assignedUsers?: string[];
  defaultDueDays?: number; // Default number of days from assignment to due date
  introductionAudioUrl?: string;
  introductionAudioDuration?: number;
  audioGeneratedAt?: string;
  // Months a pass stays valid — one setting driving both certificate expiry
  // and the automatic refresher. null = once-and-done.
  renewalMonths?: number | null;
  // New starters get this module automatically, following its rollout by
  // department. false = never; due days = how long they get.
  autoAssignNewStarters?: boolean;
  autoAssignDueDays?: number;
  // Do agency staff need to do this module? (user decision, 4 Nov 2026).
  // false = select-all / department assigning, new-starter auto-assign and
  // refresher inserts leave agency staff out; an admin can still hand-pick
  // one after a warning. New modules must answer it in the wizard.
  agencyStaffRequired?: boolean;
}

interface QuizContextType {
  quizzes: Quiz[];
  addQuiz: (quiz: Quiz) => Promise<void>;
  removeQuiz: (quizId: string) => Promise<void>;
  updateQuiz: (quiz: Quiz) => Promise<void>;
  loadQuizzes: () => Promise<void>;
  loading: boolean;
  /** True once the quiz list has been fetched successfully at least once.
   *  An empty `quizzes` array means nothing until this is true — on a cold
   *  page load there is a window before the first fetch even starts where
   *  `loading` is still false. */
  hasLoaded: boolean;
  error: string | null;
}

const QuizContext = createContext<QuizContextType>({
  quizzes: [],
  addQuiz: async () => {},
  removeQuiz: async () => {},
  updateQuiz: async () => {},
  loadQuizzes: async () => {},
  loading: true,
  hasLoaded: false,
  error: null,
});

export const QuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, profile } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const loadingRef = useRef(false);

  // Initialize storage and load quizzes only once on mount
  useEffect(() => {
    const init = async () => {
      try {
        const storageInitialized = await initializeStorage();
        if (!storageInitialized) {
          logger.warn('Storage initialization failed. Some features may be limited.');
        }
      } catch (error) {
        logger.error('Failed to initialize storage:', error);
        setError(error instanceof Error ? error.message : 'An error occurred during initialization');
      } finally {
        setIsInitialized(true);
      }
    };

    init();
  }, []);

  // Load quizzes only when initialized and user changes
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    
    loadQuizzes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isInitialized]);

  const loadQuizzes = async () => {
    logger.log('QuizContext: Attempting to load quizzes...');
    if (loadingRef.current) {
      logger.log('QuizContext: Already loading quizzes, skipping duplicate call');
      return;
    }

    try {
      loadingRef.current = true;
      setIsLoading(true);
      setError(null);
      
      if (!user) {
        logger.log('QuizContext: No user found, clearing quizzes.');
        setQuizzes([]);
        return;
      }

      // Alphabetical by design (user decision, 20 Jul): the manual drag-reorder
      // feature is gone, and quizzes.order is a vestigial column no longer read.
      const { data: quizzesData, error: quizzesError } = await supabase
        .from('quizzes')
        .select('*')
        .order('title', { ascending: true });

      logger.log('QuizContext: Fetched quizzes data:', { quizzesData, quizzesError });

      if (quizzesError) {
        logger.error('QuizContext: Error loading quizzes:', quizzesError);
        throw quizzesError;
      }

      // Then get all questions. Admins read the table, answers included (the
      // editor needs them); everyone else gets learner_questions(), the same
      // rows minus the answer and explanation of every marked question, which
      // check_answer hands back once they have answered. Asked of the database,
      // not read from the profile, which may not have loaded yet: an admin
      // served the learner copy would save a module with its answers wiped.
      const { data: isAdmin, error: roleError } = await supabase.rpc('is_admin');
      if (roleError) {
        logger.error('QuizContext: Error checking role:', roleError);
        throw roleError;
      }
      const { data: questionsData, error: questionsError } = isAdmin
        ? await supabase.from('questions').select('*').order('order', { ascending: true })
        : await supabase.rpc('learner_questions');

      logger.log('QuizContext: Fetched questions data:', { questionsData, questionsError });

      if (questionsError) {
        logger.error('QuizContext: Error loading questions:', questionsError);
        throw questionsError;
      }

      // Group questions by quiz_id
      const questionsByQuiz = (questionsData as DbQuestion[] | null)?.reduce((acc: Record<string, DbQuestion[]>, question) => {
        if (!acc[question.quiz_id]) {
          acc[question.quiz_id] = [];
        }
        acc[question.quiz_id].push(question);
        return acc;
      }, {}) || {};

      logger.log('QuizContext: Processing fetched data...');

      // Transform Supabase data to our Quiz interface
      const transformedQuizzes: Quiz[] = await Promise.all(
        (quizzesData || []).map(async (dbQuiz: DbQuiz) => {

          const quizQuestions = questionsByQuiz[dbQuiz.id] || [];

          // Sort questions by order
          const sortedQuestions = [...quizQuestions]
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(mapDbQuestion);

          return {
            id: String(dbQuiz.id),
            title: dbQuiz.title,
            description: dbQuiz.description,
            passingScore: dbQuiz.passing_score,
            mediaUrl: dbQuiz.media_url,
            isEnabled: dbQuiz.is_enabled,
            isHidden: dbQuiz.is_hidden,
            order: dbQuiz.order || 0,
            hasIntroduction: dbQuiz.has_introduction,
            introductionTitle: dbQuiz.introduction_title,
            introductionContent: dbQuiz.introduction_content,
            introductionMediaUrl: dbQuiz.introduction_media_url,
            introductionMinReadTime: dbQuiz.introduction_min_read_time ?? 0,
            timeLimitMinutes: dbQuiz.time_limit_minutes ?? null,
            introductionAudioUrl: dbQuiz.introduction_audio_url,
            introductionAudioDuration: dbQuiz.introduction_audio_duration,
            audioGeneratedAt: dbQuiz.audio_generated_at,
            renewalMonths: dbQuiz.renewal_months ?? null,
            autoAssignNewStarters: dbQuiz.auto_assign_new_starters ?? true,
            autoAssignDueDays: dbQuiz.auto_assign_due_days ?? 14,
            agencyStaffRequired: dbQuiz.agency_staff_required ?? true,
            questions: sortedQuestions,
            assignedUsers: dbQuiz.assigned_users || [],
          };
        })
      );

      logger.log(`QuizContext: Setting ${transformedQuizzes.length} transformed quizzes.`);
      setQuizzes(transformedQuizzes);
      setHasLoaded(true);
    } catch (err) {
      logger.error('QuizContext: Error in loadQuizzes catch block:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while loading quizzes');
      setQuizzes([]); // Reset quizzes on error
    } finally {
      setIsLoading(false);
      loadingRef.current = false;
    }
  };

  const addQuiz = async (quiz: Quiz) => {
    if (!user) throw new Error('Must be logged in to create a module');
    if (!profile) throw new Error('User profile not loaded');

    let createdQuizId: string | null = null;

    try {
      logger.log('Starting quiz creation process');
      logger.log('User auth ID:', user.id);
      logger.log('Profile ID (public.users.id):', profile.id);

      // Validate quiz data
      if (!quiz.title) throw new Error('Module title is required');
      if (!quiz.description) throw new Error('Module description is required');
      if (quiz.passingScore < 0 || quiz.passingScore > 100) throw new Error('Invalid passing score');
      // Note: Quizzes can be saved without questions (as placeholders/drafts)

      // Create the module first
      // IMPORTANT: Use profile.id (public.users.id) NOT user.id (auth.users.id)
      // The quizzes.created_by column has a foreign key to public.users.id
      const dbQuiz = await quizService.createQuiz({
        created_by: profile.id,
        title: quiz.title,
        description: quiz.description,
        passing_score: quiz.passingScore,
        media_url: quiz.mediaUrl,
        is_enabled: quiz.isEnabled,
        is_hidden: quiz.isHidden,
        order: quiz.order || 0,
        has_introduction: quiz.hasIntroduction,
        introduction_title: quiz.introductionTitle,
        introduction_content: quiz.introductionContent,
        introduction_media_url: quiz.introductionMediaUrl,
        introduction_min_read_time: quiz.introductionMinReadTime ?? 0,
        time_limit_minutes: quiz.timeLimitMinutes ?? null,
        renewal_months: quiz.renewalMonths ?? null,
        agency_staff_required: quiz.agencyStaffRequired ?? true,
        questions: [] // Add empty questions array to satisfy type
      });

      if (!dbQuiz) {
        throw new Error('Failed to create module - no quiz data returned');
      }

      createdQuizId = dbQuiz.id;
      logger.log('Quiz created successfully with ID:', createdQuizId);

      // Create questions only if there are any
      if (quiz.questions.length > 0) {
        logger.log('Proceeding to create', quiz.questions.length, 'questions');

        const questions = quiz.questions.map((q, index) => {
          // Validate each question
          if (q.type === 'click-for-answer') {
            if (!q.answerQuestion) throw new Error('Click-for-answer question requires question text');
          } else {
            if (!q.text) throw new Error('Question text is required');
          }
          if (q.type === 'multiple-choice' && (!q.options || q.options.length < 2)) {
            throw new Error('Multiple choice questions must have at least 2 options');
          }
          if (q.type === 'multiple-choice' && (!q.correctOptions || q.correctOptions.length === 0)) {
            throw new Error('Multiple choice questions must have at least one correct answer');
          }

          return {
            quiz_id: dbQuiz.id,
            type: q.type,
            text: q.text,
            // Without this, questions are created with order = NULL. Anything
            // sorting by order then sees every question tie, so a brand new quiz
            // presents its questions in whatever order the database happened to
            // return. updateQuiz has always persisted order; addQuiz did not.
            order: q.order ?? index,
            thumbnail_url: q.thumbnailUrl || '',
            options: q.options || [],
            correct_options: q.correctOptions || [],
            explanation: q.explanation || '',
            number_of_guesses: 1,
            hotspots: q.hotspots || [],
            video_url: q.videoUrl || '',
            video_type: q.videoType || 'file',
            video_start_time: q.videoStartTime || 0,
            video_end_time: q.videoEndTime || 0,
            minimum_read_time: q.minimumReadTime,
            image_orientation: q.imageOrientation,
            policy_url: q.policyUrl || '',
            // Audio support fields
            question_audio_url: q.questionAudioUrl || '',
            explanation_audio_url: q.explanationAudioUrl || '',
            question_audio_duration: q.questionAudioDuration,
            explanation_audio_duration: q.explanationAudioDuration,
            use_audio: q.useAudio || false,
            // Video question specific audio fields
            video_intro_audio_url: q.videoIntroAudioUrl || '',
            video_intro_audio_duration: q.videoIntroAudioDuration,
            video_question_audio_url: q.videoQuestionAudioUrl || '',
            video_question_audio_duration: q.videoQuestionAudioDuration,
            audio_text_hash: q.audioTextHash || null,
            weight: q.weight || 0,
          };
        });

        const createdQuestions = await quizService.createQuestions(questions);

        if (!createdQuestions) {
          throw new Error('Failed to create questions - no question data returned');
        }

        logger.log('Questions created successfully');

        // Brief delay to ensure questions are fully committed and visible via JOINs
        // before the edge function tries to fetch them (prevents race condition)
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Trigger server-side audio generation for the new quiz's questions
        if (createdQuizId) {
          logger.log('Triggering server-side audio generation for new quiz:', createdQuizId);
          try {
            const { unsubscribe } = await generateAudioServerSide(createdQuizId, (progress) => {
              logger.log(`Audio generation progress: ${progress.completedItems}/${progress.totalItems} (${progress.status})`);
              if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
                unsubscribe();
                // Reload quizzes to pick up the newly generated audio URLs
                if (progress.status === 'completed') {
                  logger.log('Audio generation completed, reloading quizzes to get audio URLs');
                  loadQuizzes();
                }
              }
            });
          } catch (audioError) {
            logger.error('Audio generation failed:', audioError);
            // Don't throw - quiz creation was successful, audio is a bonus
          }
        }
      } else {
        logger.log('Quiz saved without questions (placeholder/draft)');
      }

      // Reload quizzes to get the latest data
      await loadQuizzes();
    } catch (err) {
      logger.error('Error in addQuiz:', err);

      // Clean up the created quiz if it exists
      if (createdQuizId) {
        try {
          logger.log('Cleaning up quiz due to error');
          await supabase
            .from('quizzes')
            .delete()
            .eq('id', createdQuizId);
          
          // Remove the module from local state as well
          setQuizzes(prev => prev.filter(q => q.id !== createdQuizId));
        } catch (cleanupErr) {
          logger.error('Error cleaning up quiz:', cleanupErr);
        }
      }

      if (err instanceof Error) {
        throw new Error(`Failed to create module: ${err.message}`);
      } else if (typeof err === 'object' && err !== null) {
        throw new Error(`Failed to create module: ${JSON.stringify(err)}`);
      } else {
        throw new Error('Failed to create module: Unknown error');
      }
    }
  };

  const removeQuiz = async (quizId: string) => {
    if (!user) throw new Error('Must be logged in to delete a module');

    try {
      // Snapshot BEFORE deleting — this is what the Backups tab's "Deleted
      // Modules" section restores from. Fail closed: if the snapshot can't be
      // taken, the delete does not happen. Deleting without a safety net is
      // the bug this exists to fix (a module deleted on 21 Jul 2026 was
      // unrecoverable).
      await quizService.backupService.createBackup(quizId, 'pre_delete');

      // Media files (audio AND video) deliberately stay in storage: restore
      // re-creates the rows with their original ids, so the stored URLs keep
      // working. Orphaned files only accumulate for deletions never restored,
      // and storage is cheap next to an unrecoverable module.

      // Delete all questions associated with the module
      const { error: questionsError } = await supabase
        .from('questions')
        .delete()
        .eq('quiz_id', quizId);

      if (questionsError) throw questionsError;

      // Then delete the module itself
      // Note: RLS policies handle permission checks - admins can delete any quiz,
      // regular users can only delete their own quizzes
      const { error: quizError } = await supabase
        .from('quizzes')
        .delete()
        .eq('id', quizId);

      if (quizError) throw quizError;

      setQuizzes(prev => prev.filter(q => q.id !== quizId));
    } catch (err) {
      logger.error('Error removing quiz:', err);
      throw err;
    }
  };

  const updateQuiz = async (quiz: Quiz) => {
    if (!user) throw new Error('Must be logged in to update a module');

    try {
      // Create automatic backup before making changes
      try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const backup = await quizService.backupService.createBackup(quiz.id);
        logger.log('Created automatic backup before update');
      } catch (backupError) {
        logger.error('Error creating automatic backup:', backupError);
        // Continue with update even if backup fails
      }

      // Prepare update object with only defined fields
      const updateFields: any = {
        is_hidden: quiz.isHidden
      };

      // Only include fields that are actually provided
      if (quiz.title !== undefined) updateFields.title = quiz.title;
      if (quiz.description !== undefined) updateFields.description = quiz.description;
      if (quiz.passingScore !== undefined) updateFields.passing_score = quiz.passingScore;
      if (quiz.mediaUrl !== undefined) updateFields.media_url = quiz.mediaUrl;
      if (quiz.isEnabled !== undefined) updateFields.is_enabled = quiz.isEnabled;
      if (quiz.order !== undefined) updateFields.order = quiz.order;
      if (quiz.hasIntroduction !== undefined) updateFields.has_introduction = quiz.hasIntroduction;
      if (quiz.introductionTitle !== undefined) updateFields.introduction_title = quiz.introductionTitle;
      if (quiz.introductionContent !== undefined) updateFields.introduction_content = quiz.introductionContent;
      if (quiz.introductionMediaUrl !== undefined) updateFields.introduction_media_url = quiz.introductionMediaUrl;
      if (quiz.introductionMinReadTime !== undefined) updateFields.introduction_min_read_time = quiz.introductionMinReadTime;
      if (quiz.timeLimitMinutes !== undefined) updateFields.time_limit_minutes = quiz.timeLimitMinutes;
      if (quiz.introductionAudioUrl !== undefined) updateFields.introduction_audio_url = quiz.introductionAudioUrl;
      if (quiz.introductionAudioDuration !== undefined) updateFields.introduction_audio_duration = quiz.introductionAudioDuration;
      if (quiz.assignedUsers !== undefined) updateFields.assigned_users = quiz.assignedUsers;
      // Explicit null clears the renewal (back to once-and-done); undefined
      // means the caller didn't touch it.
      if (quiz.renewalMonths !== undefined) updateFields.renewal_months = quiz.renewalMonths;
      if (quiz.autoAssignNewStarters !== undefined) updateFields.auto_assign_new_starters = quiz.autoAssignNewStarters;
      if (quiz.autoAssignDueDays !== undefined) updateFields.auto_assign_due_days = quiz.autoAssignDueDays;
      if (quiz.agencyStaffRequired !== undefined) updateFields.agency_staff_required = quiz.agencyStaffRequired;

      // Update quiz with only the necessary fields
      const { error: quizError } = await supabase
        .from('quizzes')
        .update(updateFields)
        .eq('id', quiz.id);

      if (quizError) {
        logger.error('Error updating quiz:', quizError);
        throw new Error(`Failed to update module: ${quizError.message}`);
      }

      // Handle question updates: always process questions to handle deletions
      if (quiz.questions !== undefined) {
        logger.log('Updating questions for quiz:', quiz.id);
        
        // First, get current questions from database
        const { data: currentQuestions, error: getCurrentError } = await supabase
          .from('questions')
          .select('id')
          .eq('quiz_id', quiz.id);

        if (getCurrentError) {
          logger.error('Error getting current questions:', getCurrentError);
          throw new Error(`Failed to get current questions: ${getCurrentError.message}`);
        }

        // Find questions that need to be deleted (exist in DB but not in new questions array)
        const newQuestionIds = quiz.questions.map(q => q.id).filter(id => id); // Only existing questions have IDs
        const questionsToDelete = (currentQuestions || [])
          .filter(q => !newQuestionIds.includes(q.id))
          .map(q => q.id);

        logger.log('Questions to delete:', questionsToDelete);
        logger.log('Questions to upsert:', newQuestionIds);

        // Delete removed questions
        if (questionsToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('questions')
            .delete()
            .in('id', questionsToDelete);

          if (deleteError) {
            logger.error('Error deleting questions:', deleteError);
            throw new Error(`Failed to delete questions: ${deleteError.message}`);
          }
          logger.log('Successfully deleted questions:', questionsToDelete.length);
        }

        // Upsert remaining questions (only if there are any)
        if (quiz.questions.length > 0) {
          const mappedQuestions = quiz.questions.map(q => ({
                id: q.id,
                quiz_id: quiz.id,
                type: q.type,
                text: q.text,
                thumbnail_url: q.thumbnailUrl || '',
                options: q.options || [],
                correct_options: q.correctOptions || [],
                explanation: q.explanation || '',
                number_of_guesses: q.numberOfGuesses || 1,
                hotspots: q.hotspots || [],
                video_url: q.videoUrl || '',
                video_type: q.videoType || 'file',
                video_start_time: q.videoStartTime || 0,
                video_end_time: q.videoEndTime || 0,
                minimum_read_time: q.minimumReadTime,
                image_orientation: q.imageOrientation,
                policy_url: q.policyUrl || '',
                order: q.order || 0,
                // Audio support fields
                question_audio_url: q.questionAudioUrl || '',
                explanation_audio_url: q.explanationAudioUrl || '',
                question_audio_duration: q.questionAudioDuration,
                explanation_audio_duration: q.explanationAudioDuration,
                use_audio: q.useAudio || false,
                // Video question specific audio fields
                video_intro_audio_url: q.videoIntroAudioUrl || '',
                video_intro_audio_duration: q.videoIntroAudioDuration,
                video_question_audio_url: q.videoQuestionAudioUrl || '',
                video_question_audio_duration: q.videoQuestionAudioDuration,
                key_takeaways_heading: q.keyTakeawaysHeading || null,
                answer_question: q.answerQuestion || null,
                answer_text: q.answerText || null,
                audio_text_hash: q.audioTextHash || null,
              }));

          logger.log('DEBUG: First question being sent to DB:', JSON.stringify(mappedQuestions[0], null, 2));
          logger.log('DEBUG: All key_takeaways_heading values:', mappedQuestions.map(q => ({ id: q.id, type: q.type, heading: q.key_takeaways_heading })));

          const { error: questionsError } = await supabase
            .from('questions')
            .upsert(mappedQuestions, { onConflict: 'id' });

          if (questionsError) {
            logger.error('Error updating questions:', questionsError);
            throw new Error(`Failed to update questions: ${questionsError.message}`);
          }

          // Only generate audio if questions were actually modified
          // Skip audio generation if this is just a simple property update (like isEnabled toggle)
          const hasQuestionChanges = questionsToDelete.length > 0 || quiz.questions.length > 0;

          if (hasQuestionChanges) {
            logger.log('Triggering server-side audio generation for quiz:', quiz.id);
            try {
              const { unsubscribe } = await generateAudioServerSide(quiz.id, (progress) => {
                logger.log(`Audio generation progress: ${progress.completedItems}/${progress.totalItems} (${progress.status})`);
                if (progress.status === 'completed' || progress.status === 'failed' || progress.status === 'cancelled') {
                  unsubscribe();
                  // Reload quizzes to pick up the newly generated audio URLs
                  if (progress.status === 'completed') {
                    logger.log('Audio generation completed, reloading quizzes to get audio URLs');
                    loadQuizzes();
                  }
                }
              });
            } catch (audioError) {
              logger.error('Audio generation failed:', audioError);
              // Don't throw - quiz update was successful, audio generation is a bonus feature
              // User can manually regenerate audio later if needed
            }
          } else {
            logger.log('Skipping audio generation - no question changes detected');
          }
        }
      }

      // Reload quizzes to get the latest data
      await loadQuizzes();
    } catch (error) {
      logger.error('Error during quiz update:', error);
      throw error;
    }
  };

  return (
    <QuizContext.Provider
      value={{
        quizzes,
        addQuiz,
        removeQuiz,
        updateQuiz,
        loadQuizzes,
        loading: isLoading,
        hasLoaded,
        error,
      }}
    >
      {children}
    </QuizContext.Provider>
  );
};

export const useQuiz = () => useContext(QuizContext);

